import classes from './MailFormComponent.module.scss'
import { useCallback, useEffect, useState } from 'react'
import { AttachFile } from '@material-ui/icons'
import {
  preloadGmailAuth,
  requestVerifiedGmailToken,
} from '../../../../../functions/gmail/gmailToken'

import { Font, PDFDownloadLink, pdf } from '@react-pdf/renderer'
import { Document, Page, Text, StyleSheet } from '@react-pdf/renderer'
import ColorButtons from '../../../ColorButtons'
import TextInput from '../../../TextInput'
import { getMessage, getSubject } from '../../../../../constants/mailTemplates'
import OrderPDF from '../../../../PDF/OrderPDF'
import {
  hideLoadingAction,
  showLoadingAction,
} from '../../../../../reducks/loading/actions'
import { useDispatch } from 'react-redux'
import { ToastContainer, toast, Bounce } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

// 送信結果を呼び出し側に返す（成功偽装を防ぐため、成否を必ず伝播する）。
// 戻り値: { ok: boolean, status: number, messageId?: string, threadId?: string, error?: any }
// 注意: ネットワーク断などで fetch 自体が失敗した場合は例外が伝播する。
// 呼び出し側で必ず捕捉し、ローディングを解除すること。
const sendEmail = async (accessToken, rawEmail) => {
  const response = await fetch(
    'https://gmail.googleapis.com/gmail/v1/users/me/messages/send',
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        raw: rawEmail,
      }),
    }
  )

  if (response.ok) {
    let data = null
    try {
      data = await response.json()
    } catch (e) {
      // 応答ボディが読めなくても送信自体は成功している
    }
    return {
      ok: true,
      status: response.status,
      messageId: data && data.id,
      threadId: data && data.threadId,
    }
  }

  let error = null
  try {
    error = await response.json()
  } catch (e) {
    // JSON でないエラー応答は無視（status のみで判断）
  }
  console.error('メール送信エラー:', response.status, error)
  return { ok: false, status: response.status, error }
}

// ReactPDFで生成するドキュメント
const styles = StyleSheet.create({
  page: { padding: 30 },
  section: { margin: 10, padding: 10, fontSize: 12 },
})

// 現在の日時を "YYYYMMDD_HHMMSS" 形式で取得
const getCurrentTimestamp = () => {
  const now = new Date()
  const yyyy = now.getFullYear()
  const mm = String(now.getMonth() + 1).padStart(2, '0')
  const dd = String(now.getDate()).padStart(2, '0')
  const hh = String(now.getHours()).padStart(2, '0')
  const mi = String(now.getMinutes()).padStart(2, '0')
  const ss = String(now.getSeconds()).padStart(2, '0')
  return `${yyyy}${mm}${dd}_${hh}${mi}${ss}`
}

// PDF生成のハング対策（応答がない場合に一定時間で打ち切り、
// 全画面ローディングが永久に残るのを防ぐ）
const PDF_TIMEOUT_MS = 30000
const withTimeout = (promise, ms, message) =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message)), ms)
    promise.then(
      (value) => {
        clearTimeout(timer)
        resolve(value)
      },
      (reason) => {
        clearTimeout(timer)
        reject(reason)
      }
    )
  })

const convertFileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      const base64Data = reader.result.split(',')[1]
      resolve(base64Data)
    }
    reader.onerror = reject
  })

const createEmail = (to, cc, from, subject, message, attachments) => {
  const encodedSubject = `=?utf-8?B?${btoa(
    unescape(encodeURIComponent(subject))
  )}?=`

  const headers = [
    `To: ${to}`,
    `From: ${from}`,
    `Subject: ${encodedSubject}`,
    'MIME-Version: 1.0',
    'Content-Type: multipart/mixed; boundary="boundary"',
  ]

  if (cc && cc.trim() !== '') {
    headers.splice(1, 0, `Cc: ${cc}`)
  }

  // メール本文
  let emailBody = `
--boundary
Content-Type: text/plain; charset="UTF-8"

${message}
`

  // 各ファイルを添付
  attachments.forEach(({ base64Data, fileName }) => {
    emailBody += `
--boundary
Content-Type: application/pdf
Content-Disposition: attachment; filename="${fileName}"
Content-Transfer-Encoding: base64

${base64Data}
`
  })

  emailBody += '--boundary--'

  const email = [...headers, '', emailBody].join('\n')

  const base64EncodedEmail = btoa(unescape(encodeURIComponent(email)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')

  return base64EncodedEmail
}
const MyDocument = () => (
  <Document>
    <Page style={styles.page}>
      <Text style={styles.section}>注文書の内容をここに記載</Text>
    </Page>
  </Document>
)

const MailFormComponent = (props) => {
  const { shop, orders, onSent, onSkip, userInfo } = props
  const dispatch = useDispatch()
  const [pdfFiles, setPdfFiles] = useState([])
  const [pdfPreviewUrls, setPdfPreviewUrls] = useState([])
  const [isSending, setIsSending] = useState(false)
  const [pdfError, setPdfError] = useState(false)
  const [emailsTo, setEmailsTo] = useState(shop.shop_to)
  const [emailsCc, setEmailsCc] = useState(shop.shop_cc)
  const [title, setTitle] = useState(getSubject(shop.shop_name))
  const [sign, setSign] = useState(userInfo.sign)
  const [content, setContent] = useState(getMessage(shop.shop_name))

  // モーダル表示時にトークンクライアントを事前初期化し、
  // 送信ボタンのクリック（ユーザージェスチャ）内でポップアップが開けるようにする。
  useEffect(() => {
    preloadGmailAuth()
  }, [])

  const handleSendEmail = async () => {
    if (isSending) return
    if (pdfFiles.length === 0) {
      alert('PDFが添付されていません')
      return
    }

    const fromEmail = userInfo.email
    setIsSending(true)
    try {
      // 送信直前に「送信ユーザ本人のアカウント」であることを検証済みの
      // トークンを取得する（初回は同意画面・2回目以降は無音/キャッシュ）。
      // 別アカウントが選ばれていた場合はアカウント選択を促し、
      // それでも一致しない場合は例外で中断される（誤アカウント送信の防止）。
      const accessToken = await requestVerifiedGmailToken(fromEmail)

      const message = `${content}\n\n${sign}`

      // ファイルがBase64に変換されているかどうかで処理を分岐
      const attachments = await Promise.all(
        pdfFiles.map(async (file) => {
          if (file.base64Data) {
            // 既にBase64に変換済みの場合
            return { base64Data: file.base64Data, fileName: file.fileName }
          } else {
            // BlobをBase64に変換する場合
            const base64Data = await convertFileToBase64(file)
            return { base64Data, fileName: file.name }
          }
        })
      )

      const rawEmail = createEmail(
        emailsTo,
        emailsCc,
        fromEmail,
        title,
        message,
        attachments
      )

      dispatch(showLoadingAction('送信中...'))
      let result
      try {
        result = await sendEmail(accessToken, rawEmail)
        // トークン無効 (401) の場合のみ、同意付きで再認可（アカウント再検証込み）し
        // 1回だけリトライ。
        if (!result.ok && result.status === 401) {
          const retryToken = await requestVerifiedGmailToken(fromEmail, {
            prompt: 'consent',
          })
          result = await sendEmail(retryToken, rawEmail)
        }
      } finally {
        // 送信リクエストが例外で終わってもローディングを残さない（固着防止）
        dispatch(hideLoadingAction())
      }

      // 実際に送信成功した時のみ成功扱い（「送ったつもりで未送信」を防ぐ）。
      if (!result.ok) {
        const detail =
          (result.error && result.error.error && result.error.error.message) ||
          `HTTP ${result.status}`
        alert(
          `メール送信に失敗しました（${detail}）。\n` +
            'ステータスは発注済のままです。このまま再度「送信」するか、後から「再発行」で送信してください。'
        )
        return
      }

      toast.info(' 送信しました!', {
        containerId: 'MailForm',
        position: 'top-left',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'light',
        transition: Bounce,
      })
      onSent(shop.shop_id, {
        messageId: result.messageId,
        threadId: result.threadId,
        sentAt: new Date(),
      })
    } catch (e) {
      // トークン取得失敗・アカウント不一致・通信断など。
      // どの経路でも必ずここへ落とし、画面が固まったままにならないようにする。
      console.error('メール送信処理エラー:', e)
      if (e && e.name === 'AccountMismatchError') {
        alert(
          `Google で選択されたアカウント（${e.actualEmail}）が送信ユーザ（${fromEmail}）と一致しないため、送信を中止しました。\n正しいアカウントを選択して再度お試しください。`
        )
      } else if (e && e.name === 'TokenVerificationError') {
        alert(
          '送信アカウントの確認ができなかったため、送信を中止しました。\n通信環境を確認して再度お試しください。'
        )
      } else {
        alert(
          'メールを送信できませんでした。通信環境を確認して再度お試しください。\nステータスは発注済のままです（メールは「再発行」からも送信できます）。'
        )
      }
    } finally {
      setIsSending(false)
    }
  }

  // PDFをBase64形式で生成し、プレビューURLをセット。
  // 生成が失敗・ハングしても「注文書作成中...」が固着しないよう、
  // タイムアウトとエラーハンドリングを設ける（従来はここで例外が漏れると
  // ローディングが永久に残り、メール送信フォームが開かないように見えた）。
  const generateInitialPdf = useCallback(async () => {
    dispatch(showLoadingAction('注文書作成中...'))
    setPdfError(false)
    try {
      const timestamp = getCurrentTimestamp()
      const fileName = `注文書_${shop.shop_name}_${timestamp}.pdf`
      const pdfBlob = await withTimeout(
        pdf(<OrderPDF orderForms={orders} />).toBlob(),
        PDF_TIMEOUT_MS,
        '注文書PDFの作成がタイムアウトしました'
      )

      // プレビュー用URLを生成
      const previewUrl = URL.createObjectURL(pdfBlob)
      setPdfPreviewUrls([previewUrl])

      // PDFをBase64に変換
      const base64Pdf = await convertFileToBase64(pdfBlob)
      setPdfFiles([{ base64Data: base64Pdf, fileName: fileName }])
    } catch (e) {
      // 失敗してもフォームは表示したままにし、「PDFを再作成」で復帰できるようにする
      console.error('注文書PDF作成エラー:', e)
      setPdfError(true)
    } finally {
      dispatch(hideLoadingAction())
    }
  }, [orders, shop.shop_name, dispatch])

  useEffect(() => {
    if (orders.length > 0 && shop.shop_name) {
      generateInitialPdf()
    }
  }, [orders, shop.shop_name, generateInitialPdf])

  const checkSendable = () => {
    return Boolean(emailsTo)
  }

  const handlePdfUpload = (e) => {
    const files = Array.from(e.target.files)
    setPdfFiles(files)

    // プレビュー用のURLを生成
    const urls = files.map((file) => URL.createObjectURL(file))
    setPdfPreviewUrls(urls)
  }

  return (
    <div className="c-section-wrapin">
      <div className={classes.container}>
        <div className={classes.sender}>
          送信元(Gmail): <span>{userInfo.email}</span>
        </div>
        <div className={classes.inputMultiLine}>
          <TextInput
            fullWidth={true}
            label={'TO:宛先'}
            multiline={true}
            required={true}
            rows={2}
            value={emailsTo}
            type={'text'}
            disabled={false}
            onChange={(e) => setEmailsTo(e.target.value)}
          />
          {<p className={classes.error}>{!emailsTo ? '必須です' : ''}</p>}
        </div>
        <div className={classes.adjusterMinus}></div>
        <div className={classes.inputMultiLine}>
          <TextInput
            fullWidth={true}
            label={'CC'}
            multiline={true}
            required={true}
            rows={2}
            value={emailsCc}
            type={'text'}
            disabled={false}
            onChange={(e) => setEmailsCc(e.target.value)}
          />
          {<p className={classes.error}></p>}
        </div>
        <div className={classes.inputMultiLine}>
          <TextInput
            fullWidth={true}
            label={'件名'}
            multiline={true}
            required={true}
            rows={2}
            value={title}
            type={'text'}
            disabled={false}
            onChange={(e) => setTitle(e.target.value)}
          />
          {<p className={classes.error}></p>}
        </div>
        <div className={classes.inputMultiLine}>
          <TextInput
            fullWidth={true}
            label={'本文'}
            multiline={true}
            required={true}
            rows={10}
            value={content}
            type={'text'}
            disabled={false}
            onChange={(e) => setContent(e.target.value)}
          />
          {<p className={classes.error}></p>}
        </div>
        <div className={classes.inputMultiLine}>
          <TextInput
            fullWidth={true}
            label={'署名'}
            multiline={true}
            required={true}
            rows={6}
            value={sign}
            type={'text'}
            disabled={false}
            onChange={(e) => setSign(e.target.value)}
            placeholder={'プロフィール編集で設定できます'}
          />
          {<p className={classes.error}></p>}
        </div>
        <div className={classes.adjuster}></div>
        {/* PDFの添付とプレビュー */}
        <div className={classes.inputMultiLine}>
          <label className={classes.attachedLabel} htmlFor="attachPdf">
            <AttachFile className={classes.icon} />
            注文書（PDF）を添付
          </label>
          <input
            id="attachPdf"
            style={{ display: 'none' }}
            type="file"
            accept="application/pdf"
            multiple
            onChange={handlePdfUpload}
          />
          {pdfPreviewUrls?.length > 0 ? (
            <div className={classes.filenames}>
              {pdfPreviewUrls.map((url, index) => (
                <div key={index}>
                  <a href={url} target="_blank" rel="noopener noreferrer">
                    {pdfFiles[index] &&
                      (pdfFiles[index].fileName
                        ? pdfFiles[index].fileName
                        : pdfFiles[index].name)}
                  </a>
                </div>
              ))}
            </div>
          ) : (
            <>
              <div style={{ margin: '5px 0' }}></div>
              <p className={classes.error}>ファイルが添付されていません</p>
            </>
          )}
          {pdfError && (
            <>
              <p className={classes.error}>
                注文書PDFの作成に失敗しました。「PDFを再作成」を押すか、通信環境を確認して再度お試しください。
              </p>
              <div style={{ margin: '5px 0' }}></div>
              <ColorButtons
                label={'PDFを再作成'}
                color="primary"
                onClick={generateInitialPdf}
                width={160}
                height={28}
                bottom={0}
              />
            </>
          )}
        </div>
        <div className={classes.buttons}>
          <ColorButtons
            label={isSending ? '送信中...' : '送信'}
            color="primary"
            onClick={handleSendEmail}
            width={200}
            height={32}
            bottom={0}
            disabled={!checkSendable() || pdfFiles.length === 0 || isSending}
          />
          <ColorButtons
            label={'送信しない'}
            color="grey"
            onClick={() => onSkip(shop.shop_id)}
            width={200}
            height={32}
            bottom={0}
            disabled={isSending}
          />
        </div>
      </div>
    </div>
  )
}

export default MailFormComponent
